import { useState, useMemo, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingDown } from "lucide-react";
import { YearSelector } from '@/components/common/YearSelector';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { format, startOfYear, endOfYear, eachMonthOfInterval, startOfMonth, endOfMonth, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Category, Conta } from "@/types/database";
import { getEffectiveTransactionDate } from '@/lib/utils';

interface Transaction {
  id: string;
  tipo: "entrada" | "saida";
  valor: number;
  data: string;
  foi_paga: boolean;
  ignorar_transacao: boolean;
  categoria_id?: string;
  categoria?: Category;
}

interface YearlyExpensesReportProps {
  transactions: Transaction[];
  selectedDate?: Date;
  categories?: Category[];
}

export function YearlyExpensesReport({ transactions, selectedDate, categories = [] }: YearlyExpensesReportProps) {
  // Buscar contas para calcular datas efetivas
  const { data: accounts = [] } = useQuery({
    queryKey: ['contas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contas')
        .select('*')
        .eq('ativo', true);
      
      if (error) throw error;
      return data || [];
    }
  });

  // Estado para categoria selecionada
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const expenseCategories = useMemo(() => {
    return (categories || []).filter((c) => c && (c.tipo === 'saida' || c.tipo === 'ambos'));
  }, [categories]);

  useEffect(() => {
    if (selectedCategory !== 'all') {
      const exists = expenseCategories.some((c) => c.nome === selectedCategory);
      if (!exists) setSelectedCategory('all');
    }
  }, [expenseCategories, selectedCategory]);

  // Helper function to find the latest year with expense data
  const getLatestYearWithData = useCallback((transactions: Transaction[]) => {
    if (!transactions || transactions.length === 0) {
      return new Date().getFullYear();
    }
    
    // Filtrar despesas válidas
    const expenses = transactions.filter(t => {
      if (!t || t.tipo !== 'saida' || t.ignorar_transacao) return false;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const date = getEffectiveTransactionDate(t as any, accounts as Conta[]);
      return date && date instanceof Date && !isNaN(date.getTime());
    });
    
    if (expenses.length === 0) {
      // Fallback para qualquer transação válida
      const allValid = transactions.filter(t => {
        if (!t || t.ignorar_transacao) return false;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const date = getEffectiveTransactionDate(t as any, accounts as Conta[]);
        return date && date instanceof Date && !isNaN(date.getTime());
      });
      
      if (allValid.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const years = allValid.map(t => getEffectiveTransactionDate(t as any, accounts as Conta[]).getFullYear());
        return Math.max(...years);
      }
      return new Date().getFullYear();
    }
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const years = expenses.map(t => getEffectiveTransactionDate(t as any, accounts as Conta[]).getFullYear());
    return Math.max(...years);
  }, [accounts]);

  const [selectedYear, setSelectedYear] = useState(() => {
    if (selectedDate) {
      return selectedDate.getFullYear();
    }
    return new Date().getFullYear();
  });

  // Update selected year when selectedDate prop changes
  useEffect(() => {
    if (selectedDate) {
      setSelectedYear(selectedDate.getFullYear());
    }
  }, [selectedDate]);

  // Update selected year when transactions are loaded (only if no selectedDate is provided)
  useEffect(() => {
    if (!selectedDate && transactions && transactions.length > 0) {
      const latestYear = getLatestYearWithData(transactions);
      setSelectedYear(latestYear);
    }
  }, [transactions, selectedDate, getLatestYearWithData]);

  const yearlyData = useMemo(() => {
    
    const yearStart = startOfYear(new Date(Date.UTC(selectedYear, 0, 1)));
    const yearEnd = endOfYear(new Date(Date.UTC(selectedYear, 0, 1)));
    console.log(`📅 Período do ano (UTC base): ${yearStart.toISOString()} até ${yearEnd.toISOString()}`);

    // Base de meses para o ano selecionado
    const months = eachMonthOfInterval({ start: yearStart, end: yearEnd });
    const baseMonthly = months.map(month => ({
      month,
      monthIndex: month.getUTCMonth(),
      labelShort: format(month, 'MMM', { locale: ptBR }),
      labelFull: format(month, 'MMMM', { locale: ptBR })
    }));

    // Agregar valores por mês usando data efetiva
    const monthIndexToTotal = new Map<number, number>();

    for (const t of transactions) {
      if (!t || t.tipo !== 'saida' || t.ignorar_transacao) continue;

      // Filtrar por categoria selecionada
      if (selectedCategory !== 'all') {
        const transactionCategory = t.categoria?.nome || t.categoria_id;
        if (transactionCategory !== selectedCategory) {
          continue;
        }
      }
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const txDate = getEffectiveTransactionDate(t as any, accounts as Conta[]);
      if (!(txDate instanceof Date) || isNaN(txDate.getTime())) continue;

      const txYear = txDate.getFullYear();
      const txMonth = txDate.getMonth();
      
      if (txYear !== selectedYear) continue;

      const prev = monthIndexToTotal.get(txMonth) || 0;
      monthIndexToTotal.set(txMonth, prev + Number(t.valor || 0));
    }

    const monthlyExpenses = baseMonthly.map(m => {
      const total = monthIndexToTotal.get(m.monthIndex) || 0;

      return {
        month: m.labelShort,
        fullMonth: m.labelFull,
        value: total,
        monthIndex: m.monthIndex
      };
    });

    const yearTotal = monthlyExpenses.reduce((sum, month) => sum + month.value, 0);

    return { monthlyExpenses, yearTotal };
  }, [transactions, selectedYear, selectedCategory, accounts]);

  const handlePreviousYear = () => {
    setSelectedYear(prev => prev - 1);
  };

  const handleNextYear = () => {
    setSelectedYear(prev => prev + 1);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Total Summary Card */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base font-medium text-muted-foreground">
            Total Despesas por Ano
            {selectedCategory !== 'all' && (
              <span className="text-xs font-normal text-foreground block">
                Categoria: {selectedCategory}
              </span>
            )}
          </CardTitle>
          <div className="p-2 rounded-md bg-destructive text-destructive-foreground">
            <TrendingDown className="h-4 w-4" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-base font-bold text-destructive">
            {formatCurrency(yearlyData.yearTotal)}
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Janeiro - Dezembro {selectedYear}
          </p>
        </CardContent>
      </Card>

      {/* Enhanced Year Navigation */}
      <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-4">
        <div className="hidden sm:block" />
        <div className="flex justify-center">
          <YearSelector
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
          />
        </div>
        {/* Filtro de Categoria */}
        <div className="w-full sm:w-auto flex sm:justify-end">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Filtrar por categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as categorias</SelectItem>
              {expenseCategories.map((category) => (
                <SelectItem key={category.id} value={category.nome}>
                  {category.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base">Despesas por ano</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={yearlyData.monthlyExpenses}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="month" 
                    className="text-xs fill-muted-foreground"
                  />
                  <YAxis 
                    className="text-xs fill-muted-foreground"
                    tickFormatter={(value) => 
                      new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      }).format(value)
                    }
                  />
                  <Tooltip 
                    formatter={(value: number) => [formatCurrency(value), 'Despesas']}
                    labelFormatter={(label) => `${label} ${selectedYear}`}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="hsl(var(--destructive))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--destructive))', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Monthly List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base">Detalhes Anuais</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3">
              {yearlyData.monthlyExpenses.map((month, index) => (
                <div key={index} className="flex justify-between items-center py-2 border-b border-border last:border-b-0">
                  <span className="text-sm font-medium capitalize">
                    {month.fullMonth}
                  </span>
                  <span className="text-sm font-mono">
                    {formatCurrency(month.value)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}